"use client";

import React, { useEffect, useRef, useMemo } from "react";
import { VariantOption } from "./VariantOptions";
import { Switch } from "@/components/ui/switch";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { Eye, Upload, Trash2 } from "lucide-react";
import { ShippingDetails, Metafield } from "@/types/models/product";
import VariantGenerator from "./VariantGenerator";
import { generateImageSrc } from "@/utils/helper";

export type PaymentType = 'PREPAID' | 'POSTPAID' | 'SEMIPREPAID';

export type VariantCombination = {
    id: string;
    options: string[]; // Array of selected values for each option group
    optionString: string; // Display string like "Blue / Large"
    image?: string; // Legacy single image (for backward compatibility)
    images?: string[]; // Multiple images array (asset IDs for backend)
    variantAssets?: Array<{ asset_id: string; asset?: { path: string; original_name?: string; [key: string]: any } }>; // Asset objects for preview
    price: number; // in cents
    listingPrice: number; // in cents
    sku: string;
    hsCode?: string;
    mid?: string;
    barcode?: string;
    trackInventory: boolean;
    inventory: number;
    title: string;
    description: string;
    shippingDetails: ShippingDetails;
    metafields: Metafield[] | Record<string, string>;
    supported_payment_types?: PaymentType[]; // Array of supported payment types
    semi_prepaid_amount?: number; // Amount in paise for semi-prepaid payment type
};

type Props = {
    variantOptions: VariantOption[];
    variants: VariantCombination[];
    deletedVariants?: VariantCombination[];
    onChange: (variants: VariantCombination[]) => void;
    onVariantDetail: (variant: VariantCombination) => void;
    onImageUpload?: (variantId: string) => void;
    onReenableVariant?: (variant: VariantCombination) => void;
    onEnableVariant?: (variant: VariantCombination) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
};

// Types for change detection
type ChangeType = {
    type:
        | "INITIAL"
        | "FULL_REGENERATION"
        | "PROPERTY_VALUE_ADDED"
        | "PROPERTY_VALUE_REMOVED"
        | "PROPERTY_VALUE_UPDATED"
        | "PROPERTY_ADDED"
        | "PROPERTY_REMOVED";
    propertyIndex?: number;
    newValue?: string;
    oldValue?: string;
    removedValue?: string;
    newPropertyValues?: string[];
};

/**
 * Efficiently detect the type of change between option sets
 * This enables targeted updates instead of full regeneration
 */
function detectChangeType(
    previous: VariantOption[],
    current: VariantOption[]
): ChangeType {
    // Initial case - no previous options
    if (previous.length === 0 && current.length > 0) {
        return { type: "INITIAL" };
    }

    // Property count changed
    if (previous.length !== current.length) {
        if (previous.length < current.length) {
            // New property added
            const newProperty = current[current.length - 1];
            return {
                type: "PROPERTY_ADDED",
                propertyIndex: current.length - 1,
                newPropertyValues: newProperty.values,
            };
        } else {
            // Property removed - need full regeneration for simplicity
            return { type: "FULL_REGENERATION" };
        }
    }

    // Check for property-level changes
    for (let i = 0; i < previous.length; i++) {
        const prevOption = previous[i];
        const currentOption = current[i];

        // Property title changed - full regeneration needed
        if (prevOption.title !== currentOption.title) {
            return { type: "FULL_REGENERATION" };
        }

        // Check for value changes
        const prevValues = new Set(prevOption.values);
        const currentValues = new Set(currentOption.values);

        // Values added
        const addedValues = [...currentValues].filter(
            (v) => !prevValues.has(v)
        );
        if (
            addedValues.length === 1 &&
            currentValues.size === prevValues.size + 1
        ) {
            return {
                type: "PROPERTY_VALUE_ADDED",
                propertyIndex: i,
                newValue: addedValues[0],
            };
        }

        // Values removed
        const removedValues = [...prevValues].filter(
            (v) => !currentValues.has(v)
        );
        if (
            removedValues.length === 1 &&
            currentValues.size === prevValues.size - 1
        ) {
            return {
                type: "PROPERTY_VALUE_REMOVED",
                propertyIndex: i,
                removedValue: removedValues[0],
            };
        }

        // Value updated (one removed, one added, same count)
        if (
            addedValues.length === 1 &&
            removedValues.length === 1 &&
            currentValues.size === prevValues.size
        ) {
            return {
                type: "PROPERTY_VALUE_UPDATED",
                propertyIndex: i,
                oldValue: removedValues[0],
                newValue: addedValues[0],
            };
        }

        // Multiple changes in same property - full regeneration
        if (addedValues.length > 1 || removedValues.length > 1) {
            return { type: "FULL_REGENERATION" };
        }
    }

    // No significant changes detected
    return { type: "FULL_REGENERATION" };
}


export default function VariantTable({
    variantOptions,
    variants,
    deletedVariants = [],
    onChange,
    onVariantDetail,
    onImageUpload,
    onReenableVariant,
    onEnableVariant,
    onKeyDown,
}: Props) {
    // Log props on each render
    console.log('[VariantTable] Render with props:', {
        variantOptionsCount: variantOptions.length,
        variantsCount: variants.length,
        variantOptions: variantOptions.map(o => ({ title: o.title, values: o.values })),
        variants: variants.map(v => ({ id: v.id, optionString: v.optionString, sku: v.sku })),
    });

    // Use a ref to maintain the generator instance across renders
    const generatorRef = useRef(new VariantGenerator());
    const previousOptionsRef = useRef<VariantOption[]>([]);
    const isInitializedRef = useRef(false);
    // Ref to access current variants without adding to effect dependencies (avoids infinite loop)
    const variantsRef = useRef<VariantCombination[]>(variants);
    variantsRef.current = variants;

    // Debug logs removed - component is quiet in production

    // Memoize options comparison to detect changes efficiently
    const optionsChanged = useMemo(() => {
        const current = JSON.stringify(variantOptions);
        const previous = JSON.stringify(previousOptionsRef.current);
        return current !== previous;
    }, [variantOptions]);

    // Handle efficient updates when options change
    useEffect(() => {
        // Use ref to access variants without dependency (avoids infinite loop)
        const currentVariants = variantsRef.current;

        console.log('[VariantTable] useEffect triggered', {
            optionsChanged,
            variantOptionsLength: variantOptions.length,
            variantsLength: currentVariants.length,
            isInitialized: isInitializedRef.current,
            previousOptionsLength: previousOptionsRef.current.length,
        });

        if (!optionsChanged) {
            console.log('[VariantTable] Skipping - options not changed');
            return;
        }

        // Skip generation if no options (default variant mode)
        if (variantOptions.length === 0) {
            console.log('[VariantTable] Skipping - no options (default variant mode)');
            return;
        }

        const generator = generatorRef.current;
        const previousOptions = previousOptionsRef.current;
        const currentOptions = variantOptions;

        // Detect type of change for optimal update strategy
        const changeType = detectChangeType(previousOptions, currentOptions);
        console.log('[VariantTable] Change type detected:', changeType.type);

        // ALWAYS check if variants have API data - not just on INITIAL load
        // This handles cases where the component rendered before API data arrived
        if (currentVariants.length > 0) {
            // Check if variants have API data (have real IDs, not generated ones)
            // API IDs are UUIDs, generated IDs start with 'variant-', 'disabled-', 'new-variant-', etc.
            const hasApiData = currentVariants.some((variant) => {
                const id = variant.id || '';
                const isGeneratedId = id.startsWith('variant-') ||
                                     id.startsWith('disabled-') ||
                                     id.startsWith('new-variant-') ||
                                     id.startsWith('default-');
                return !isGeneratedId && id.length > 0;
            });

            // Check if existing variants match the options structure
            const existingOptionsMatch = currentVariants.every((variant) => {
                if (variant.id.startsWith('disabled-')) return true; // Skip disabled variants
                return variant.options.length === currentOptions.length;
            });

            console.log('[VariantTable] API data check:', {
                hasApiData,
                existingOptionsMatch,
                variantIds: currentVariants.map(v => v.id),
                variantOptionsLengths: currentVariants.map(v => v.options.length),
                currentOptionsLength: currentOptions.length,
            });

            if (hasApiData && existingOptionsMatch) {
                // Variants have API data and match options - skip regeneration
                console.log('[VariantTable] Skipping regeneration - preserving API data');
                isInitializedRef.current = true;
                previousOptionsRef.current = [...currentOptions];
                return;
            }
        }

        // Mark as initialized after first generation
        isInitializedRef.current = true;
        console.log('[VariantTable] Will regenerate variants');

        let updatedVariants: VariantCombination[];

        switch (changeType.type) {
            case "INITIAL":
            case "FULL_REGENERATION":
                // Full regeneration for complex changes
                updatedVariants = generator.generateVariants(currentOptions);
                break;

            case "PROPERTY_VALUE_ADDED":
                // Use full regeneration instead of incremental update
                updatedVariants = generator.generateVariants(currentOptions);
                break;

            case "PROPERTY_VALUE_REMOVED":
                // Use full regeneration instead of incremental update
                updatedVariants = generator.generateVariants(currentOptions);
                break;

            case "PROPERTY_VALUE_UPDATED":
                // Use full regeneration instead of incremental update
                updatedVariants = generator.generateVariants(currentOptions);
                break;

            case "PROPERTY_ADDED":
                // New property added
                updatedVariants = generator.addProperty(
                    changeType.newPropertyValues!,
                    currentOptions
                );
                break;

            case "PROPERTY_REMOVED":
                // Property removed
                updatedVariants = generator.removeProperty(
                    changeType.propertyIndex!,
                    currentOptions
                );
                break;

            default:
                // Fallback to full regeneration
                updatedVariants = generator.generateVariants(currentOptions);
        }

        // Always update variants when options change (remove the equality check that could prevent updates)
        console.log('[VariantTable] Calling onChange with regenerated variants:', {
            count: updatedVariants.length,
            variants: updatedVariants.map(v => ({ id: v.id, optionString: v.optionString, sku: v.sku })),
        });
        onChange(updatedVariants);

        // Update previous options reference
        previousOptionsRef.current = [...currentOptions];
    }, [variantOptions, optionsChanged, onChange]); // Do NOT include variants - causes infinite loop

    // Initialize generator with current variants on mount
    useEffect(() => {
        if (variants.length > 0) {
            // Sync generator state with existing variants
            generatorRef.current.generateVariants(variantOptions);
        }
    }, []); // Empty dependency array - only run on mount

    const updateVariant = (
        id: string,
        field: keyof VariantCombination,
        value: unknown
    ) => {
        // Always use simple update to preserve manual changes and "Apply to All" changes
        // The generator's internal state might not reflect recent updates
        onChange(
            variants.map((variant) =>
                variant.id === id ? { ...variant, [field]: value } : variant
            )
        );
    };

    const updateShippingDetail = (
        variantId: string,
        field: keyof ShippingDetails,
        value: unknown
    ) => {
        // Find the variant to get its current shipping details
        const targetVariant = variants.find((v) => v.id === variantId);
        if (!targetVariant) return;

        // Create updated shipping details
        const updatedShippingDetails = {
            ...targetVariant.shippingDetails,
            [field]: value,
        };

        // Always use simple update to preserve manual changes and "Apply to All" changes
        onChange(
            variants.map((variant) =>
                variant.id === variantId
                    ? {
                          ...variant,
                          shippingDetails: updatedShippingDetails,
                      }
                    : variant
            )
        );
    };

    const deleteVariant = (variantId: string) => {
        // If this is the last or only variant, don't allow deletion
        if (variants.length <= 1) {
            return;
        }

        // Use simple filtering to preserve manual changes and "Apply to All" changes
        const filteredVariants = variants.filter(
            (variant) => variant.id !== variantId
        );
        onChange(filteredVariants);
    };

    // Note: We no longer show empty state since there's always at least a default variant

    return (
        <div className="space-y-4" data-variant-table>
            <style>{`
                /* Highlight inputs/selects/textareas immediately on focus inside the variant table */
                [data-variant-table] input:focus,
                [data-variant-table] select:focus,
                [data-variant-table] textarea:focus {
                    outline: none;
                    border-color: rgba(99,102,241,0.9) !important; /* indigo-600 */
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important;
                    transition: box-shadow 0.12s ease-in-out, border-color 0.12s ease-in-out;
                }

                /* Also show a subtle focus-visible ring for keyboard navigation */
                [data-variant-table] input:focus-visible,
                [data-variant-table] select:focus-visible,
                [data-variant-table] textarea:focus-visible {
                    box-shadow: 0 0 0 3px rgba(99,102,241,0.18) !important;
                }
            `}</style>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-medium text-foreground">
                        Variant Combinations
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        {variants.length} variant
                        {variants.length !== 1 ? "s" : ""} generated from your
                        option groups
                    </p>
                </div>
            </div>

            <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full min-w-[1200px]">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[120px]">
                                Options
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[80px]">
                                Image
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[100px]">
                                Price ($)
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[100px]">
                                Listing Price ($)
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[120px]">
                                SKU
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[120px]">
                                Barcode
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[80px]">
                                Track Inv.
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[80px]">
                                Inventory
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[100px]">
                                Weight
                            </th>
                            <th className="text-left p-3 text-xs font-medium text-muted-foreground min-w-[120px]">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {variants.map((variant) => {
                            const isDisabled = variant.id.startsWith('disabled-');
                            
                            if (isDisabled) {
                                // Render disabled row
                                return (
                                    <tr
                                        key={variant.id}
                                        className="border-b border-border bg-muted/30"
                                    >
                                        <td className="p-3" colSpan={10}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="text-sm font-medium text-muted-foreground">
                                                        {variant.optionString}
                                                    </div>
                                                    <span className="px-2 py-1 text-xs rounded bg-muted text-muted-foreground">
                                                        Disabled
                                                    </span>
                                                </div>
                                                <ButtonPrimary
                                                    type="button"
                                                    size="sm"
                                                    onClick={() => onEnableVariant?.(variant)}
                                                >
                                                    Enable Variant
                                                </ButtonPrimary>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            
                            // Render normal variant row
                            return (
                            <tr
                                key={variant.id}
                                className="border-b border-border"
                            >
                                <td className="p-3">
                                    <div className="text-sm font-medium text-foreground">
                                        {variant.optionString}
                                    </div>
                                </td>
                                <td className="p-3">
                                    <div className="relative">
                                        {variant.variantAssets && variant.variantAssets.length > 0 ? (
                                            <div className="relative group">
                                                <button
                                                    type="button"
                                                    onClick={() => onImageUpload?.(variant.id)}
                                                    className="w-12 h-12 border-2 border-border rounded-md overflow-hidden hover:border-primary transition-colors relative"
                                                >
                                                    <img
                                                        src={generateImageSrc(variant.variantAssets[0].asset?.path || '', 100, 100)}
                                                        alt={variant.variantAssets[0].asset?.original_name || "Variant"}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            console.error("Failed to load variant image:", variant.variantAssets?.[0]);
                                                            e.currentTarget.style.display = 'none';
                                                        }}
                                                    />
                                                    {variant.variantAssets.length > 1 && (
                                                        <span className="absolute bottom-0 right-0 bg-primary text-white text-[10px] px-1 rounded-tl">
                                                            +{variant.variantAssets.length - 1}
                                                        </span>
                                                    )}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => onImageUpload?.(variant.id)}
                                                className="w-12 h-12 border-2 border-dashed border-border rounded-md flex items-center justify-center hover:border-primary transition-colors"
                                            >
                                                <Upload size={16} className="text-muted-foreground" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                                <td className="p-3">
                                    <input
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        placeholder="0.00"
                                        value={variant.price / 100}
                                        onChange={(e) =>
                                            updateVariant(
                                                variant.id,
                                                "price",
                                                Number(e.target.value) * 100
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={onKeyDown}
                                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background"
                                    />
                                </td>
                                <td className="p-3">
                                    <input
                                        type="number"
                                        step={0.01}
                                        min={0}
                                        placeholder="0.00"
                                        value={variant.listingPrice / 100}
                                        onChange={(e) =>
                                            updateVariant(
                                                variant.id,
                                                "listingPrice",
                                                Number(e.target.value) * 100
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={onKeyDown}
                                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background"
                                    />
                                </td>
                                <td className="p-3">
                                    <input
                                        type="text"
                                        placeholder="SKU"
                                        value={variant.sku}
                                        onChange={(e) =>
                                            updateVariant(
                                                variant.id,
                                                "sku",
                                                e.target.value
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={onKeyDown}
                                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background"
                                    />
                                </td>
                                <td className="p-3">
                                    <input
                                        type="text"
                                        placeholder="Barcode"
                                        value={variant.barcode || ""}
                                        onChange={(e) =>
                                            updateVariant(
                                                variant.id,
                                                "barcode",
                                                e.target.value
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={onKeyDown}
                                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background"
                                    />
                                </td>
                                <td className="p-3">
                                    <div className="flex justify-center">
                                        <Switch
                                            checked={variant.trackInventory}
                                            onCheckedChange={(checked) =>
                                                updateVariant(
                                                    variant.id,
                                                    "trackInventory",
                                                    checked
                                                )
                                            }
                                        />
                                    </div>
                                </td>
                                <td className="p-3">
                                    <input
                                        type="number"
                                        min={0}
                                        placeholder="0"
                                        disabled={!variant.trackInventory}
                                        value={variant.inventory}
                                        onChange={(e) =>
                                            updateVariant(
                                                variant.id,
                                                "inventory",
                                                Number(e.target.value)
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onKeyDown={onKeyDown}
                                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background disabled:opacity-50"
                                    />
                                </td>
                                <td className="p-3">
                                    <div className="flex space-x-1">
                                        <input
                                            type="number"
                                            min={0}
                                            placeholder="0"
                                            value={
                                                variant.shippingDetails.weight
                                            }
                                            onChange={(e) =>
                                                updateShippingDetail(
                                                    variant.id,
                                                    "weight",
                                                    Number(e.target.value)
                                                )
                                            }
                                            onFocus={(e) => e.target.select()}
                                            onKeyDown={onKeyDown}
                                            className="w-16 px-1 py-1 text-xs border border-border rounded bg-background"
                                        />
                                        <select
                                            value={
                                                variant.shippingDetails
                                                    .weightUnit
                                            }
                                            onChange={(e) =>
                                                updateShippingDetail(
                                                    variant.id,
                                                    "weightUnit",
                                                    e.target.value
                                                )
                                            }
                                            onKeyDown={onKeyDown}
                                            className="w-16 px-1 py-1 text-xs border border-border rounded bg-background"
                                        >
                                            <option value="g">g</option>
                                            <option value="kg">kg</option>
                                        </select>
                                    </div>
                                </td>
                                <td className="p-3">
                                    <div className="flex space-x-2">
                                        <ButtonPrimary
                                            type="button"
                                            size="sm"
                                            onClick={() =>
                                                onVariantDetail(variant)
                                            }
                                        >
                                            <Eye size={14} className="mr-1" />
                                            View More
                                        </ButtonPrimary>
                                        {variants.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    deleteVariant(variant.id)
                                                }
                                                className="px-2 py-1 text-xs border border-destructive text-destructive hover:bg-destructive hover:text-white rounded transition-colors"
                                                title="Delete variant"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
            {deletedVariants && deletedVariants.length > 0 && (
                <div className="mt-6">
                    <div className="mb-3">
                        <h4 className="text-sm font-medium text-foreground">Deleted Variants</h4>
                        <p className="text-xs text-muted-foreground">
                            These variants were removed from the product. You can re-enable them below.
                        </p>
                    </div>
                    <div className="overflow-x-auto border border-border rounded-lg bg-muted/30">
                        <table className="w-full">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">
                                        Options
                                    </th>
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">
                                        SKU
                                    </th>
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">
                                        Price ($)
                                    </th>
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">
                                        Inventory
                                    </th>
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {deletedVariants.map((variant) => (
                                    <tr
                                        key={variant.id}
                                        className="border-b border-border opacity-60"
                                    >
                                        <td className="p-3">
                                            <div className="text-sm font-medium text-foreground line-through">
                                                {variant.optionString}
                                            </div>
                                            <div className="text-xs text-destructive mt-1">
                                                Deleted
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <div className="text-xs text-muted-foreground">
                                                {variant.sku || "N/A"}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <div className="text-xs text-muted-foreground">
                                                ${(variant.price / 100).toFixed(2)}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <div className="text-xs text-muted-foreground">
                                                {variant.inventory}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <ButtonPrimary
                                                type="button"
                                                size="sm"
                                                onClick={() => onReenableVariant?.(variant)}
                                            >
                                                Re-enable
                                            </ButtonPrimary>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
